import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  Platform,
  Image,
} from "react-native";
import React, { useState, useEffect, useCallback } from "react";
import { MaterialIcons } from "@expo/vector-icons";
import Loader from "../../../../components/loader/Loader";
import { useFocusEffect } from "expo-router";
import StandardNavigation from "../../../../components/navigation/StandardNavigation";
import Animated, { FadeInDown } from "react-native-reanimated";
import { Toast } from "react-native-toast-notifications";
import i18n from "../../../../locales/i18n";
import customAxios from "../../../../utils/customAxios";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { authActions } from "../../../../store/slices/authSlice";
import { appActions } from "../../../../store/slices/appSlice";
import wilayasData from "../../../../constants/wilayas.json";
import WebStoreInfoScreen from "../../../../components/web/WebStoreInfoScreen";

// Helper function to get display name for wilaya based on locale
const getWilayaDisplayName = (wilayaNameFr: string, locale: string) => {
  const wilaya = wilayasData.wilayas.find((w) => w.name === wilayaNameFr);
  if (!wilaya) return wilayaNameFr;
  return locale === "ar" ? wilaya.name_ar : wilaya.name;
};

const index = () => {
  const dispatch = useDispatch();
  const { user, loading, refetch } = useSelector(
    (state: RootState) => state.auth,
  );
  const { locale } = useSelector((state: RootState) => state.app);

  // State for editable fields
  const [storename, setStorename] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categories, setCategories] = useState<
    Array<{ id_category: number; label: string }>
  >([]);
  const [saveLoading, setSaveLoading] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);

  // Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Wilaya modal state
  const [showWilayaModal, setShowWilayaModal] = useState(false);

  const userData = user as {
    id_user?: number;
    username?: string;
    email?: string;
    storename?: string;
    phone?: string;
    location?: string;
    description?: string;
    photo?: string;
    role?: string;
    is_verified?: boolean;
    created_at?: string;
    payed?: boolean;
    categoryId?: number;
  } | null;

  useEffect(() => {
    if (userData) {
      setStorename(userData.storename || "");
      setDescription(userData.description || "");
      setLocation(userData.location || "");
      setCategoryId(userData.categoryId || null);
    }
  }, [userData]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await customAxios.get("/marketplace/categories");
        setCategories(result.data.categories || []);
      } catch (error) {
        // silently ignore
      }
    };
    fetchCategories();
  }, []);

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch]),
  );

  // Fetch user data on mount and when refetch changes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await customAxios.get("/users/check");
        dispatch(authActions.setUser(res.data.userinfo));
      } catch (error) {
        // silently ignore
      }
    };
    fetchUserData();
  }, [dispatch, refetch]);

  // Cancel edit mode and reset form fields
  const handleCancelEdit = () => {
    if (userData) {
      setStorename(userData.storename || "");
      setDescription(userData.description || "");
      setLocation(userData.location || "");
      setCategoryId(userData.categoryId || null);
    }
    setIsEditing(false);
  };

  // Save store info
  const handleSaveStoreInfo = async () => {
    setSaveLoading(true);
    try {
      // Only include fields that have actually changed
      const updatedFields: any = {};

      if (storename !== (userData?.storename || "")) {
        updatedFields.storename = storename;
      }
      if (description !== (userData?.description || "")) {
        updatedFields.description = description;
      }
      if (location !== (userData?.location || "")) {
        updatedFields.location = location;
      }
      if (categoryId !== (userData?.categoryId || null)) {
        updatedFields.categoryId = categoryId;
      }

      // If no fields changed
      if (Object.keys(updatedFields).length === 0) {
        Toast.show(i18n.t("noChangesToSave"), { type: "info" });
        setSaveLoading(false);
        return;
      }

      await customAxios.patch(`/users/update`, updatedFields, {
        withCredentials: true,
      });

      Toast.show(i18n.t("profileUpdateSuccess"), { type: "success" });
      dispatch(authActions.toggleRefetch());
      setIsEditing(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      if (errorMessage) {
        Toast.show(errorMessage, { type: "danger" });
      } else {
        Toast.show(i18n.t("profileUpdateFailed"), { type: "danger" });
      }
    } finally {
      setSaveLoading(false);
    }
  };

  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebStoreInfoScreen />;
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
      {/* Standard Navigation */}
      <StandardNavigation
        title={i18n.t("storeInfo")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
      />
      {/* Edit/Update Button */}
      <View className="px-5 pt-3 pb-2 bg-[#0596D0]">
        {!isEditing ? (
          <TouchableOpacity
            onPress={() => setIsEditing(true)}
            className="px-4 py-3 rounded-xl flex-row items-center justify-center bg-white"
          >
            <MaterialIcons name="edit" size={20} color="#0596D0" />
            <Text className="font-medium text-base ml-2 text-[#0596D0]">
              {i18n.t("changedata")}
            </Text>
          </TouchableOpacity>
        ) : (
          <View className="flex-row gap-3">
            <TouchableOpacity
              onPress={handleCancelEdit}
              className="flex-1 px-4 py-3 rounded-xl flex-row items-center justify-center bg-gray-500"
            >
              <MaterialIcons name="close" size={20} color="white" />
              <Text className="font-medium text-base ml-2 text-white">
                {i18n.t("cancel")}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleSaveStoreInfo}
              disabled={saveLoading}
              className="flex-1 px-4 py-3 rounded-xl flex-row items-center justify-center bg-green-500"
            >
              {saveLoading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <>
                  <MaterialIcons name="save" size={20} color="white" />
                  <Text className="font-medium text-base ml-2 text-white">
                    {i18n.t("save")}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>

      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 16,
          paddingBottom: 100,
        }}
      >
        {/* Store Name Field */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(200)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("storeName")}
          </Text>
          <View
            className={`rounded-xl px-6 py-4 border ${
              isEditing
                ? "bg-white border-gray-100"
                : "bg-gray-50 border-gray-100"
            }`}
          >
            {isEditing ? (
              <TextInput
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#1F2937",
                }}
                value={storename}
                onChangeText={setStorename}
                placeholder={i18n.t("defaultStoreName")}
                autoCapitalize="none"
              />
            ) : (
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#374151",
                }}
              >
                {storename || i18n.t("defaultStoreName")}
              </Text>
            )}
          </View>
        </Animated.View>

        {/* Store Description Field */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(300)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("storeDescription")}
          </Text>
          <View
            className={`rounded-xl px-6 py-4 border ${
              isEditing
                ? "bg-white border-gray-100"
                : "bg-gray-50 border-gray-100"
            }`}
          >
            {isEditing ? (
              <TextInput
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#1F2937",
                }}
                value={description}
                onChangeText={setDescription}
                placeholder={i18n.t("defaultStoreDescription")}
                multiline={true}
                numberOfLines={3}
                autoCapitalize="none"
              />
            ) : (
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: "#374151",
                }}
              >
                {description || i18n.t("defaultStoreDescription")}
              </Text>
            )}
          </View>
        </Animated.View>

        {/* Store Category Field */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(400)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("storeCategory")}
          </Text>
          <TouchableOpacity
            onPress={() => isEditing && setShowCategoryModal(true)}
            disabled={!isEditing}
            className={`rounded-xl px-6 py-4 border ${
              isEditing
                ? "bg-white border-blue-400 border-2"
                : categoryId === null
                  ? "bg-yellow-50 border-yellow-300"
                  : "bg-gray-50 border-gray-100"
            }`}
          >
            <View
              className={`flex-row items-center justify-between ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: isEditing
                    ? "#2196F3"
                    : categoryId === null
                      ? "#9CA3AF"
                      : "#374151",
                  fontWeight: isEditing ? "600" : "500",
                }}
              >
                {(categoryId !== null &&
                  categories.find((cat) => cat.id_category === categoryId)
                    ?.label) ||
                  (categoryId === null
                    ? i18n.t("selectCategory")
                    : categories.length > 0
                      ? i18n.t("selectCategory")
                      : "Loading categories...")}
              </Text>
              {isEditing && (
                <MaterialIcons
                  name={
                    locale === "ar"
                      ? "keyboard-arrow-left"
                      : "keyboard-arrow-down"
                  }
                  size={24}
                  color="#2196F3"
                />
              )}
            </View>
          </TouchableOpacity>
        </Animated.View>

        {/* Location Field */}
        <Animated.View
          entering={FadeInDown.duration(400).delay(500)}
          className="mb-6"
        >
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("location")}
          </Text>
          <TouchableOpacity
            onPress={() => isEditing && setShowWilayaModal(true)}
            disabled={!isEditing}
            className={`rounded-xl px-6 py-4 border ${
              isEditing
                ? "bg-white border-blue-400 border-2"
                : location
                  ? "bg-gray-50 border-gray-100"
                  : "bg-yellow-50 border-yellow-300"
            }`}
          >
            <View
              className={`flex-row items-center justify-between ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <Text
                style={{
                  fontSize: 16,
                  textAlign: locale === "ar" ? "right" : "left",
                  color: isEditing
                    ? "#2196F3"
                    : location
                      ? "#374151"
                      : "#9CA3AF",
                  fontWeight: isEditing ? "600" : "500",
                }}
              >
                {location
                  ? getWilayaDisplayName(location, locale)
                  : i18n.t("selectWilaya") || "Select Wilaya"}
              </Text>
              {isEditing && (
                <MaterialIcons
                  name={
                    locale === "ar"
                      ? "keyboard-arrow-left"
                      : "keyboard-arrow-down"
                  }
                  size={24}
                  color="#2196F3"
                />
              )}
            </View>
          </TouchableOpacity>
        </Animated.View>
      </ScrollView>

      {/* Category Selection Modal */}
      <Modal
        visible={showCategoryModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowCategoryModal(false)}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
          }}
        >
          <View
            style={{
              backgroundColor: "white",
              borderRadius: 20,
              padding: 24,
              width: "100%",
              maxWidth: 400,
              maxHeight: "80%",
            }}
          >
            <View
              className={`flex-row items-center justify-between mb-6 ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <Text className="text-xl font-bold text-gray-800">
                {i18n.t("selectCategory")}
              </Text>
              <TouchableOpacity
                onPress={() => setShowCategoryModal(false)}
                className="p-2"
              >
                <MaterialIcons name="close" size={24} color="#6B7280" />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              {categories.map((category) => (
                <TouchableOpacity
                  key={category.id_category}
                  onPress={() => {
                    setCategoryId(category.id_category);
                    setShowCategoryModal(false);
                  }}
                  className={`py-4 px-4 rounded-xl mb-2 ${
                    categoryId === category.id_category
                      ? "bg-blue-50 border-2 border-blue-400"
                      : "bg-gray-50 border border-gray-200"
                  }`}
                >
                  <View
                    className={`flex-row items-center ${
                      locale === "ar" ? "flex-row-reverse" : "flex-row"
                    }`}
                  >
                    <View
                      className={`w-6 h-6 rounded-full border-2 mr-3 items-center justify-center ${
                        categoryId === category.id_category
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      } ${locale === "ar" ? "ml-3 mr-0" : "mr-3"}`}
                    >
                      {categoryId === category.id_category && (
                        <MaterialIcons name="check" size={16} color="white" />
                      )}
                    </View>
                    <Text
                      className={`text-base ${
                        categoryId === category.id_category
                          ? "text-blue-700 font-semibold"
                          : "text-gray-700"
                      }`}
                      style={{
                        textAlign: locale === "ar" ? "right" : "left",
                      }}
                    >
                      {category.label}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View className="mt-6 pt-4 border-t border-gray-200">
              <TouchableOpacity
                onPress={() => setShowCategoryModal(false)}
                className="bg-gray-500 py-3 px-6 rounded-xl"
              >
                <Text className="text-white text-center font-semibold text-base">
                  {i18n.t("cancel")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Wilaya Selection Modal */}
      <Modal
        visible={showWilayaModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowWilayaModal(false)}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
          }}
        >
          <View
            style={{
              backgroundColor: "white",
              borderRadius: 20,
              padding: 24,
              width: "100%",
              maxWidth: 400,
              maxHeight: "80%",
            }}
          >
            <View
              className={`flex-row items-center justify-between mb-6 ${
                locale === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <Text className="text-xl font-bold text-gray-800">
                {i18n.t("selectWilaya") || "Select Wilaya"}
              </Text>
              <TouchableOpacity
                onPress={() => setShowWilayaModal(false)}
                className="p-2"
              >
                <MaterialIcons name="close" size={24} color="#6B7280" />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              {wilayasData.wilayas.map((wilaya) => (
                <TouchableOpacity
                  key={wilaya.id}
                  onPress={() => {
                    setLocation(wilaya.name);
                    setShowWilayaModal(false);
                  }}
                  className={`py-4 px-4 rounded-xl mb-2 ${
                    wilaya.name === location
                      ? "bg-blue-50 border-2 border-blue-400"
                      : "bg-gray-50 border border-gray-200"
                  }`}
                >
                  <View
                    className={`flex-row items-center ${
                      locale === "ar" ? "flex-row-reverse" : "flex-row"
                    }`}
                  >
                    <View
                      className={`w-6 h-6 rounded-full border-2 items-center justify-center ${
                        wilaya.name === location
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      } ${locale === "ar" ? "ml-3 mr-0" : "mr-3"}`}
                    >
                      {wilaya.name === location && (
                        <MaterialIcons name="check" size={16} color="white" />
                      )}
                    </View>
                    <View className="flex-1">
                      <Text
                        className={`text-base ${
                          wilaya.name === location
                            ? "text-blue-700 font-semibold"
                            : "text-gray-700"
                        }`}
                        style={{
                          textAlign: locale === "ar" ? "right" : "left",
                        }}
                      >
                        {locale === "ar" ? wilaya.name_ar : wilaya.name}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View className="mt-6 pt-4 border-t border-gray-200">
              <TouchableOpacity
                onPress={() => setShowWilayaModal(false)}
                className="bg-gray-500 py-3 px-6 rounded-xl"
              >
                <Text className="text-white text-center font-semibold text-base">
                  {i18n.t("cancel")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default index;
